import type { Snapshot } from './http';
import type { TranscriptItem, Message } from './state';
import { SUPPORTED_INTERACTIONS, type InteractionMessage, type InteractionResponse, type PublicRow, type WireEnvelope, type WriteResult } from './protocol/types';
import { compareCursors, interactionCursor, mergeItems, readInteraction, readPublicMessage } from './protocol/readers';
import { isButtonsV1Presentation, isCarouselPresentation, isChoicePresentation, isDatePickerPresentation, isInteractionResponse } from './protocol/validators';
export interface InteractionHost {
  sessionId(): string | null;
  humanConnected(): boolean;
  sendFrame(frame: Record<string, unknown>): boolean;
  updateItems(fn: (items: TranscriptItem[]) => TranscriptItem[]): void;
  refreshSnapshot(): Promise<Snapshot | null>;
  onChange(): void;
  newId(): string;
  /** An engine limit rejection of a response; wait is the server's retry_after_s (undefined when giving up). */
  limit(code: 'rate_limited' | 'daily_limit_reached' | 'session_limit_reached' | 'public_chat_disabled', wait?: number): void;
  clearLimit(): void;
  later(ms: number, fn: () => void): void;
}
const MAX_LIMIT_RETRIES = 3;
const DEFAULT_RATE_WAIT_S = 5;
const LIMIT_CODES = ['rate_limited', 'daily_limit_reached', 'session_limit_reached', 'public_chat_disabled'] as const;
export type InteractionError = { code: 'interaction_not_pending' | 'interaction_suspended' | 'invalid_interaction' | 'network'; message: string };
interface Selection {
  interactionId: string;
  messageId: string;
  original: InteractionMessage;
  frame: Record<string, unknown>;
  acknowledged: boolean;
  sent: boolean;
  limited: number;
}

const activeOffer = (offer: InteractionMessage) => ['pending', 'accepted'].includes(offer.interaction_status);
export class InteractionController {
  private _active: InteractionMessage | null = null;
  private _cursor = '0';
  private _pending = false;
  private _error: InteractionError | null = null;
  private operations = new Map<string, Selection>();
  private operation: Selection | null = null;
  private generation = 0;
  private refreshInFlight = false;
  private recoveryCursor: string | null = null;
  constructor(private readonly host: InteractionHost) {}
  get active(): InteractionMessage | null { return this._active; }
  get cursor(): string { return this._cursor; }
  get pending(): boolean { return this._pending; }
  get error(): InteractionError | null { return this._error; }
  private setActive(value: InteractionMessage | null): void { this._active = value; this.host.onChange(); }
  private setCursor(value: string): void { this._cursor = value; this.host.onChange(); }
  private setSelectionPending(value: boolean): void { this._pending = value; this.host.onChange(); }
  private setInteractionError(value: InteractionError | null): void { this._error = value; this.host.onChange(); }
  private setRows(fn: (rows: PublicRow[]) => PublicRow[]): void {
    this.host.updateItems((items) => {
      let cut = items.length;
      while (cut > 0 && items[cut - 1]!.kind !== 'divider') cut -= 1;
      const tail = items.slice(cut);
      const out = fromRows(fn(toRows(tail)), tail);
      // toRows/fromRows only model messages and interactions; keep the local welcome where it was.
      tail.forEach((item, at) => { if (item.kind === 'welcome') out.splice(Math.min(at, out.length), 0, item); });
      return [...items.slice(0, cut), ...out];
    });
  }
  reset() {
    this.generation += 1;
    this.refreshInFlight = false;
    this.recoveryCursor = null;
    this.setActive(null);
    this._cursor = '0';
    this.setCursor('0');
    this.operations.clear();
    this.operation = null;
    this.setSelectionPending(false);
    this.setInteractionError(null);
  }

  private reject(operation: Selection, reason: InteractionError) {
    this.operations.delete(operation.interactionId);
    if (this.operations.size === 0) this.recoveryCursor = null;
    if (this.operation === operation) this.operation = null;
    this.setSelectionPending(false);
    this.setInteractionError(reason);
    const current = this._active;
    const restore = current?.interaction_id === operation.interactionId &&
      current.interaction_revision <= operation.original.interaction_revision;
    if (restore) this.setActive(operation.original);
    this.setRows((rows) => rows.filter((row) => row.id !== operation.messageId)
      .map((row) => restore && row.interaction_id === operation.interactionId ? readPublicMessage(operation.original)! : row));
  }

  private mergeProjection(offer: InteractionMessage | null, answer: unknown) {
    const response = readPublicMessage(answer);
    const operation = offer && this.operations.get(offer.interaction_id);
    if (operation && response?.role === 'user') {
      this.operations.delete(operation.interactionId);
      if (this.operations.size === 0) this.recoveryCursor = null;
      if (this.operation === operation) this.operation = null;
      this.setSelectionPending(false);
      this.setInteractionError(null);
    }
    const rows = [offer && readPublicMessage(offer), response?.role === 'user' ? response : null].filter((row): row is PublicRow => row !== null);
    this.setRows((current) => {
      // The continuation can arrive before the accepted-choice event. Replace
      // the optimistic answer in place so reconciliation cannot move it below
      // the assistant's reply, and discard any earlier canonical echo.
      const reconciled = operation && response?.role === 'user' &&
        current.some((row) => row.id === operation.messageId)
        ? current.flatMap((row) => row.id === operation.messageId ? [response] :
          row.id === response.id ? [] : [row])
        : current;
      return mergeItems(reconciled, rows);
    });
  }

  private refreshMissingOutcome(minimumCursor: string) {
    if (this.operations.size === 0) {
      this.recoveryCursor = null;
      return;
    }
    if (this.recoveryCursor === null || compareCursors(minimumCursor, this.recoveryCursor) > 0) {
      this.recoveryCursor = minimumCursor;
    }
    if (this.refreshInFlight) return;
    const requestedCursor = this.recoveryCursor;
    const requestedOperations = [...this.operations.values()];
    const stillUnresolved = (operation: Selection) => this.operations.get(operation.interactionId) === operation;
    const scope = this.generation;
    const sessionId = this.host.sessionId();
    this.refreshInFlight = true;
    this.setSelectionPending(true);
    void this.host.refreshSnapshot().then((snapshot) => {
      if (!snapshot) throw new Error("Snapshot unavailable");
      if (scope !== this.generation || sessionId !== this.host.sessionId() || (snapshot.session !== null && snapshot.session.id !== sessionId)) return;
      if (!requestedOperations.some(stillUnresolved)) return;
      const nextCursor = interactionCursor(snapshot.interaction_cursor);
      if (nextCursor === null || compareCursors(nextCursor, this.recoveryCursor ?? requestedCursor) < 0 || compareCursors(nextCursor, this._cursor) < 0) return;
      const offer = snapshot.active_interaction === null ? null : readInteraction(snapshot.active_interaction);
      if (snapshot.active_interaction !== null && (!offer || !activeOffer(offer))) return;
      const canonical = snapshot.messages.map(readPublicMessage).filter((row): row is PublicRow => row !== null);
      const linked = readPublicMessage(snapshot.interaction_response_message);
      if (offer) canonical.push(readPublicMessage(offer)!);
      if (linked?.role === 'user') canonical.push(linked);
      const settled = requestedOperations.filter((operation) => stillUnresolved(operation) &&
        (offer?.interaction_id !== operation.interactionId || (offer.interaction_status === 'accepted' && linked?.role === 'user')));
      const optimisticIds = new Set(settled.map((operation) => operation.messageId));
      this.setRows((rows) => mergeItems(rows.filter((row) => !optimisticIds.has(row.id)), canonical)
        .sort((left, right) => (Date.parse(left.ts) - Date.parse(right.ts)) || 0));
      settled.forEach((operation) => this.operations.delete(operation.interactionId));
      if (this.operation && settled.includes(this.operation)) this.operation = null;
      this._cursor = nextCursor;
      this.recoveryCursor = null;
      this.setCursor(nextCursor);
      this.setActive(offer);
      this.setSelectionPending(this.operation !== null);
      this.setInteractionError(null);
    }).catch(() => {
      if (scope === this.generation && requestedOperations.some(stillUnresolved)) {
        this.setInteractionError({ code: 'network', message: 'Reconnecting to confirm your selection…' });
      }
    }).finally(() => {
      if (scope === this.generation) {
        this.refreshInFlight = false;
        if (this.operations.size === 0) {
          this.recoveryCursor = null;
          return;
        }
        // A lifecycle event received during this request raises the required
        // snapshot cursor; never lose it behind the in-flight request guard.
        const required = this.recoveryCursor;
        if (required !== null && compareCursors(required, requestedCursor) > 0) this.refreshMissingOutcome(required);
      }
    });
  }

  private hasMissingOutcome(offer: InteractionMessage | null): boolean { return [...this.operations.values()].some((operation) => !offer || operation.interactionId !== offer.interaction_id); }

  applySnapshot(snapshot: Snapshot) {
    const nextCursor = interactionCursor(snapshot.interaction_cursor);
    if (nextCursor === null || !Object.prototype.hasOwnProperty.call(snapshot, 'active_interaction')) return;
    const order = compareCursors(nextCursor, this._cursor);
    if (order < 0) return;
    const offer = snapshot.active_interaction === null ? null : readInteraction(snapshot.active_interaction);
    if (snapshot.active_interaction !== null && (!offer || !activeOffer(offer))) return;
    if (order > 0 && this.hasMissingOutcome(offer)) {
      this.refreshMissingOutcome(nextCursor);
      return;
    }
    // Equal-revision polls cannot undo a local selection still awaiting its outcome.
    if (order === 0 && (this.operation || (offer && this.operations.has(offer.interaction_id)) ||
      (offer === null && this._active))) return;
    this._cursor = nextCursor;
    this.setCursor(nextCursor);
    this.setActive(offer);
    this.mergeProjection(offer, snapshot.interaction_response_message);
    if (!offer || this.operation?.interactionId !== offer.interaction_id) {
      this.operation = null;
      this.setSelectionPending(false);
    }
  }

  applyEvent(event: WireEnvelope): boolean {
    if (!['interaction_published', 'interaction_updated', 'interaction_closed'].includes(event.type)) return false;
    if (event.session_id && event.session_id !== this.host.sessionId()) return true;
    const nextCursor = interactionCursor(event.payload.interaction_cursor);
    const offer = readInteraction(event.payload);
    if (!offer || offer.interaction_status === 'preparing' || nextCursor === null || compareCursors(nextCursor, this._cursor) <= 0) return true;
    if (this.hasMissingOutcome(offer) || (!activeOffer(offer) && this.operations.has(offer.interaction_id) &&
      !readPublicMessage(event.payload.interaction_response_message))) {
      this.refreshMissingOutcome(nextCursor);
      return true;
    }
    this._cursor = nextCursor;
    this.setCursor(nextCursor);
    this.mergeProjection(offer, event.payload.interaction_response_message);
    if (activeOffer(offer)) this.setActive(offer);
    else if (this._active?.interaction_id === offer.interaction_id) this.setActive(null);
    if (this.operation && (this.operation.interactionId !== offer.interaction_id || !activeOffer(offer))) {
      this.operation = null;
      this.setSelectionPending(false);
    }
    return true;
  }

  handleWriteResult(result: WriteResult): boolean {
    const payload = result as unknown as Record<string, unknown>;
    const operation = [...this.operations.values()].find((item) => item.messageId === payload.message_id);
    if (!operation) return false;
    const limit = LIMIT_CODES.find((c) => c === payload.error);
    if (limit) {
      this.handleLimit(operation, limit, Number(payload.retry_after_s));
    } else if (payload.error || ['refused', 'failed', 'error', 'busy'].includes(String(payload.state))) {
      const code = ['interaction_not_pending', 'interaction_suspended', 'invalid_interaction', 'network'].includes(String(payload.error)) ? payload.error as InteractionError['code'] : 'invalid_interaction';
      this.reject(operation, { code, message: String(payload.error || payload.reason || 'The selection was not accepted. Please try again.') });
    } else {
      operation.acknowledged = true;
      if (this.operation === operation) this.operation = null;
      this.setSelectionPending(false);
      this.setInteractionError(null);
      this.host.clearLimit();
    }
    return true;
  }

  // Same client handling as message writes: rate_limited waits and re-sends the response, the others reject it.
  private handleLimit(operation: Selection, code: typeof LIMIT_CODES[number], given: number): void {
    const message = 'The selection was not accepted. Please try again.';
    if (code === 'rate_limited' && operation.limited < MAX_LIMIT_RETRIES) {
      const wait = Number.isFinite(given) && given > 0 ? Math.ceil(given) : DEFAULT_RATE_WAIT_S;
      operation.limited += 1;
      this.host.limit(code, wait);
      this.host.later(wait * 1000, () => {
        this.host.clearLimit();
        if (this.operations.get(operation.interactionId) !== operation) return;
        operation.sent = this.host.sendFrame(operation.frame);
      });
      return;
    }
    this.reject(operation, { code: 'invalid_interaction', message });
    this.host.limit(code, code === 'rate_limited' ? undefined : (Number.isFinite(given) && given > 0 ? Math.ceil(given) : undefined));
  }

  respond(interactionId: string, response: InteractionResponse): boolean {
    const offer = this._active;
    if (!this.host.sessionId() || this.host.humanConnected() || this.operation || !offer ||
      offer.interaction_id !== interactionId || offer.interaction_status !== 'pending' ||
      !SUPPORTED_INTERACTIONS.some((pair) => pair.tool_type === offer.tool_type && pair.schema_version === offer.schema_version) ||
      !isInteractionResponse(offer, response)) return false;
    const legacy = offer.tool_type === 'ui_buttons' && offer.schema_version === 1;
    let displayText: string | null = null;
    if (legacy && response.kind === 'selection' && isButtonsV1Presentation(offer.presentation)) {
      displayText = offer.presentation.options.find((option) => option.id === response.value)?.label ?? null;
    } else if (!legacy && response.kind === 'text' && offer.tool_type === 'ui_buttons' && isChoicePresentation(offer.presentation)) {
      displayText = response.value;
    } else if (!legacy && response.kind === 'selection' && offer.tool_type === 'ui_buttons' && isChoicePresentation(offer.presentation)) {
      displayText = offer.presentation.options.find((option) => option.id === response.value)?.label ?? null;
    } else if (response.kind === 'selection' && offer.tool_type === 'ui_carousel' && isCarouselPresentation(offer.presentation, undefined, true)) {
      displayText = offer.presentation.items.find((item) => item.id === response.value)?.title ?? null;
    } else if (response.kind === 'date' && offer.tool_type === 'ui_date_picker' && offer.schema_version === 1 &&
      isDatePickerPresentation(offer.presentation) && offer.presentation.selection_mode === 'single') {
      if ((!offer.presentation.min_date || response.value >= offer.presentation.min_date) &&
        (!offer.presentation.max_date || response.value <= offer.presentation.max_date)) displayText = response.value;
    } else if (response.kind === 'date_range' && offer.tool_type === 'ui_date_picker' && offer.schema_version === 1 &&
      isDatePickerPresentation(offer.presentation) && offer.presentation.selection_mode === 'range') {
      const { start_date: start, end_date: end } = response.value;
      if ((!offer.presentation.min_date || start >= offer.presentation.min_date) &&
        (!offer.presentation.max_date || end <= offer.presentation.max_date)) displayText = `${start} to ${end}`;
    }
    if (!displayText) return false;
    const messageId = this.host.newId();
    const operation: Selection = {
      interactionId, messageId, original: offer, acknowledged: false, sent: false, limited: 0,
      frame: { type: 'interaction_response', interaction_id: interactionId,
        ...(legacy ? { option_id: response.value } : { response }),
        message_id: messageId, idempotency_key: this.host.newId(), supported_interactions: SUPPORTED_INTERACTIONS },
    };
    this.operations.set(interactionId, operation);
    this.operation = operation;
    this.setSelectionPending(true);
    this.setInteractionError(null);
    const optimistic: InteractionMessage = { ...offer, interaction_status: 'accepted',
      ...(legacy ? { selected_option_id: response.value as string } : { response_payload: response }) };
    this.setActive(optimistic);
    this.setRows((rows) => mergeItems(rows, [readPublicMessage(optimistic)!,
      { id: messageId, type: 'message', role: 'user', content: displayText!, ts: new Date().toISOString(), local: true }]));
    operation.sent = this.host.sendFrame(operation.frame);
    return true;
  }

  onConnectionOpen(): void {
    const operation = this.operation;
    if (operation && !operation.acknowledged && !this.host.humanConnected()) {
      operation.sent = this.host.sendFrame(operation.frame);
    }
  }
}

export function toRows(items: TranscriptItem[]): PublicRow[] {
  const rows: PublicRow[] = [];
  for (const item of items) {
    if (item.kind === 'interaction') rows.push(readPublicMessage(item.interaction)!);
    else if (item.kind === 'message') {
      rows.push({ id: item.serverId ?? item.id, type: 'message', role: item.role === 'human' ? 'operator' : item.role,
        content: item.content, ts: item.createdAt });
    }
  }
  return rows;
}

export function fromRows(rows: PublicRow[], previous: TranscriptItem[]): TranscriptItem[] {
  const known = new Map<string, Message>();
  for (const item of previous) if (item.kind === 'message') { known.set(item.id, item); if (item.serverId) known.set(item.serverId, item); }
  const out: TranscriptItem[] = [];
  for (const row of rows) {
    if (row.type === 'interaction' && row.interaction_id) {
      const interaction = readInteraction(row);
      if (interaction) out.push({ kind: 'interaction', id: row.interaction_id, interaction });
      continue;
    }
    if (row.role === 'system') continue;
    const prev = known.get(row.id);
    if (prev) { out.push(prev.content === row.content ? prev : { ...prev, content: row.content }); continue; }
    if (row.local) {
      out.push({ kind: 'message', id: row.id, role: 'user', content: row.content, createdAt: row.ts, status: 'sending' });
      continue;
    }
    out.push({ kind: 'message', id: `srv:${row.id}`, serverId: row.id, role: row.role === 'operator' ? 'human' : row.role,
      content: row.content, createdAt: row.ts, status: 'sent' });
  }
  return out;
}
